import type {
  ApiDocument,
  ApiFolder,
  CreateDocumentRequest,
  CreateDocumentResponse,
  CreateFolderRequest,
  CreateFolderResponse,
  GetDocumentsResponse,
  SidebarFolderNode,
} from "@workith/shared";
import { getSharedDriveCapabilities } from "@workith/shared";
import { Button, Card, Input, Skeleton } from "@workith/ui";
import { ChevronRight, Folder, FolderPlus, Home, Plus, Search } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DocumentGrid } from "../components/document/DocumentGrid";
import { DocumentGridSkeleton } from "../components/document/DocumentGridSkeleton";
import { DocumentViewModeToggle } from "../components/document/DocumentViewModeToggle";
import { DocumentVisibilityBadge } from "../components/document/DocumentVisibilityBadge";
import { DriveItemList } from "../components/document/DriveItemList";
import {
  canDeleteFolder,
  canRenameFolder,
  FolderActions,
} from "../components/document/FolderActions";
import { useDocumentViewMode } from "../components/document/useDocumentViewMode";
import { useSidebarTree } from "../components/layout/useSidebarTree";
import api from "../lib/api";

type SelectedFolderCreationContext = Pick<ApiFolder, "scope" | "user_role"> | null | undefined;

function flattenFolderTree(folders: SidebarFolderNode[]): ApiFolder[] {
  return folders.flatMap((folder) => [folder, ...flattenFolderTree(folder.children)]);
}

export function canCreateInSelectedLocation(
  currentFolderId: string | null,
  currentFolder: SelectedFolderCreationContext
): boolean {
  if (!currentFolderId) {
    return true;
  }

  if (!currentFolder) {
    return false;
  }

  if (currentFolder.scope !== "shared_drive") {
    return true;
  }

  return getSharedDriveCapabilities(currentFolder.user_role).canWrite;
}

export function refreshDashboardAfterFolderMutation(
  fetchData: () => void,
  refreshSidebarTree: () => void
) {
  fetchData();
  refreshSidebarTree();
}

export function refreshDashboardAfterDocumentMutation(
  fetchData: () => void,
  refreshSidebarTree: () => void
) {
  fetchData();
  refreshSidebarTree();
}

export function canMutateFolder(folder: ApiFolder): boolean {
  return canRenameFolder(folder) || canDeleteFolder(folder);
}

export { canDeleteFolder, canRenameFolder };

export function DashboardPage() {
  const [documents, setDocuments] = useState<ApiDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [viewMode, setViewMode] = useDocumentViewMode();
  const [searchParams, setSearchParams] = useSearchParams();
  const currentFolderId = searchParams.get("folderId");
  const {
    data: sidebarTree,
    loading: sidebarLoading,
    refresh: refreshSidebarTree,
  } = useSidebarTree(currentFolderId);
  const navigate = useNavigate();

  const fetchData = useCallback(() => {
    setLoading(true);
    api
      .get<GetDocumentsResponse>("/api/documents")
      .then((docsData) => {
        setDocuments(docsData.documents);
      })
      .catch((err) => console.error("Failed to fetch data:", err))
      .finally(() => setLoading(false));
  }, []); // Empty deps: setState functions are stable, api methods are stable

  useEffect(() => {
    fetchData();
  }, [fetchData]); // Now we can safely include fetchData

  const folders = useMemo<ApiFolder[]>(() => {
    if (!sidebarTree) {
      return [];
    }

    const personalFolders = sidebarTree.personal.root
      ? flattenFolderTree([sidebarTree.personal.root])
      : [];
    const sharedFolders = sidebarTree.sharedDrives.flatMap((section) =>
      flattenFolderTree([section.root])
    );
    return [...personalFolders, ...sharedFolders];
  }, [sidebarTree]);

  const currentFolder = useMemo(
    () => (currentFolderId ? folders.find((folder) => folder.id === currentFolderId) : null),
    [currentFolderId, folders]
  );
  const canCreateInCurrentLocation = canCreateInSelectedLocation(currentFolderId, currentFolder);
  const handleFolderMutation = useCallback(() => {
    refreshDashboardAfterFolderMutation(fetchData, refreshSidebarTree);
  }, [fetchData, refreshSidebarTree]);
  const handleDocumentMutation = useCallback(() => {
    refreshDashboardAfterDocumentMutation(fetchData, refreshSidebarTree);
  }, [fetchData, refreshSidebarTree]);
  const openDocument = useCallback(
    (documentItem: ApiDocument) => navigate(`/editor/${documentItem.id}`),
    [navigate]
  );

  const createDocument = async () => {
    if (!canCreateInCurrentLocation) return;

    const name = prompt("Document name:");
    if (!name) return;

    try {
      const request: CreateDocumentRequest = {
        name,
        folderId: currentFolderId,
      };

      const response = await api.post<CreateDocumentRequest, CreateDocumentResponse>(
        "/api/documents",
        request
      );

      setDocuments((currentDocuments) => [response.document, ...currentDocuments]);
      refreshSidebarTree();
      navigate(`/editor/${response.document.id}`);
    } catch (err) {
      console.error("Failed to create document:", err);
    }
  };

  const createFolder = async () => {
    if (!canCreateInCurrentLocation) return;

    const folderName = prompt("Folder name:");
    if (!folderName) return;

    try {
      const request: CreateFolderRequest = {
        name: folderName,
        parentId: currentFolderId, // Create in current folder
      };

      await api.post<CreateFolderRequest, CreateFolderResponse>("/api/folders", request);

      refreshSidebarTree();
    } catch (err) {
      console.error("Failed to create folder:", err);
      const error = err as Error;
      alert(`Failed to create folder: ${error.message || "Unknown error"}`);
    }
  };

  if (loading || sidebarLoading) {
    return (
      <div className="space-y-6 p-6">
        <Skeleton className="h-8 w-48" />
        <DocumentGridSkeleton />
      </div>
    );
  }

  // Filter documents by current folder and search query
  const filteredDocuments = documents.filter((doc) => {
    const matchesSearch = doc.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesFolder = currentFolderId === null || doc.folder_id === currentFolderId;
    return matchesSearch && matchesFolder;
  });

  const visibleFolders = folders.filter((folder) => {
    if (currentFolderId === null) {
      return folder.root_kind === "personal_root" || folder.root_kind === "shared_drive_root";
    }
    return folder.parent_id === currentFolderId;
  });
  const hasVisibleItems = visibleFolders.length > 0 || filteredDocuments.length > 0;
  const documentsEmptyMessage = searchQuery
    ? "No documents match your search."
    : "No documents yet. Create your first document!";
  const showListDocumentNoResults = Boolean(searchQuery) && filteredDocuments.length === 0;

  const handleFolderClick = (folder: ApiFolder) => {
    setSearchParams({ folderId: folder.id });
  };

  const handleBackToRoot = () => {
    setSearchParams({});
  };

  // Build breadcrumb path from current folder to root
  const buildBreadcrumbPath = (): ApiFolder[] => {
    if (!currentFolderId) return [];

    const path: ApiFolder[] = [];
    let folderId: string | null = currentFolderId;

    // Traverse up the parent chain
    while (folderId) {
      const folder = folders.find((f) => f.id === folderId);
      if (!folder) break;

      path.unshift(folder); // Add to beginning of array
      folderId = folder.parent_id;
    }

    return path;
  };

  const breadcrumbPath = buildBreadcrumbPath();

  return (
    <div className="p-6">
      <div className="mb-6 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-2xl font-bold mb-2">Dashboard</h2>
          {/* Breadcrumb Navigation */}
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <button
              type="button"
              onClick={handleBackToRoot}
              className="flex items-center gap-1 hover:text-foreground"
            >
              <Home className="h-4 w-4" />
              <span>All Documents</span>
            </button>
            {breadcrumbPath.map((folder) => (
              <div key={folder.id} className="flex items-center gap-2">
                <ChevronRight className="h-4 w-4" />
                <button
                  type="button"
                  onClick={() => handleFolderClick(folder)}
                  className="hover:text-foreground"
                >
                  {folder.name}
                </button>
              </div>
            ))}
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <DocumentViewModeToggle viewMode={viewMode} onViewModeChange={setViewMode} />
          {canCreateInCurrentLocation && (
            <>
              <Button variant="outline" onClick={createFolder}>
                <FolderPlus className="mr-2 h-4 w-4" />
                New Folder
              </Button>
              <Button onClick={createDocument}>
                <Plus className="mr-2 h-4 w-4" />
                New Document
              </Button>
            </>
          )}
        </div>
      </div>

      <div className="mb-6">
        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder="Search documents..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
      </div>

      {viewMode === "list" ? (
        hasVisibleItems ? (
          <>
            <DriveItemList
              folders={visibleFolders}
              documents={filteredDocuments}
              allFolders={folders}
              onOpenFolder={handleFolderClick}
              onOpenDocument={openDocument}
              onFolderUpdate={handleFolderMutation}
              onDocumentUpdate={handleDocumentMutation}
            />
            {showListDocumentNoResults && (
              <p className="mt-4 text-muted-foreground">{documentsEmptyMessage}</p>
            )}
          </>
        ) : (
          <p className="text-muted-foreground">{documentsEmptyMessage}</p>
        )
      ) : (
        <>
          {visibleFolders.length > 0 && (
            <div className="mb-8">
              <h3 className="text-lg font-semibold mb-3">Folders</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                {visibleFolders.map((folder) => (
                  <Card key={folder.id} className="p-4 hover:shadow-md cursor-pointer relative">
                    <div className="flex items-center justify-between">
                      <button
                        type="button"
                        className="flex items-center gap-3 flex-1"
                        onClick={() => handleFolderClick(folder)}
                      >
                        <Folder className="h-8 w-8 text-muted-foreground" />
                        <span className="font-medium">{folder.name}</span>
                      </button>
                      {canMutateFolder(folder) && (
                        <FolderActions folder={folder} onUpdate={handleFolderMutation} />
                      )}
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          )}

          <div>
            <h3 className="text-lg font-semibold mb-3">
              Documents {searchQuery && `(${filteredDocuments.length} results)`}
            </h3>
            {filteredDocuments.length === 0 ? (
              <p className="text-muted-foreground">{documentsEmptyMessage}</p>
            ) : (
              <DocumentGrid
                documents={filteredDocuments}
                folders={folders}
                onOpenDocument={openDocument}
                renderBadge={(documentItem) => (
                  <DocumentVisibilityBadge visibility={documentItem.visibility} />
                )}
                onDocumentUpdate={handleDocumentMutation}
              />
            )}
          </div>
        </>
      )}
    </div>
  );
}
